/**
 * 图表色板工具（UI-SPEC-001 / UI-TOKENS.md）
 *
 * ECharts 与高德地图等 Canvas 渲染的库无法直接使用 CSS 变量，
 * 因此这里在运行时从 :root 读取设计 Token，失败时回退到与 Token 一致的静态值。
 *
 * 单一真相源仍是 src/assets/styles/design-system.css，本文件只做读取与回退。
 */

/** 与 design-system.css 中 --ds-chart-* 一致的回退值 */
export const DS_CHART_FALLBACK = {
  1: '#2456D6',
  2: '#12B3A8',
  3: '#7A5AF8',
  4: '#F79009',
  5: '#F04438',
  6: '#0BA5EC'
};

export const DS_CHART_FALLBACK_PALETTE = [
  DS_CHART_FALLBACK[1],
  DS_CHART_FALLBACK[2],
  DS_CHART_FALLBACK[3],
  DS_CHART_FALLBACK[4],
  DS_CHART_FALLBACK[5],
  DS_CHART_FALLBACK[6]
];

let cachedPalette = null;

/**
 * 读取当前生效的图表色板（带缓存，避免在渲染循环中反复访问计算样式）。
 * @returns {string[]} 六色数组
 */
export function getDsChartPalette() {
  if (cachedPalette) {
    return cachedPalette;
  }
  try {
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      const style = window.getComputedStyle(document.documentElement);
      const palette = [];
      for (let i = 1; i <= 6; i++) {
        const value = style.getPropertyValue(`--ds-chart-${i}`).trim();
        palette.push(value || DS_CHART_FALLBACK[i]);
      }
      cachedPalette = palette;
      return palette;
    }
  } catch (e) {
    // 取不到计算样式时静默回退，不影响图表渲染
  }
  cachedPalette = DS_CHART_FALLBACK_PALETTE.slice();
  return cachedPalette;
}

/**
 * 按序号取色（序号从 0 开始，超出后循环取模）。
 * @param {number} index
 * @returns {string}
 */
export function getDsChartColor(index) {
  const palette = getDsChartPalette();
  const i = Number.isFinite(index) ? Math.abs(Math.trunc(index)) : 0;
  return palette[i % palette.length];
}

/** 语义名 -> design-system.css 中的 Token 变量名 */
const DS_SEMANTIC_TOKEN = {
  primary: '--ds-primary',
  success: '--ds-success',
  warning: '--ds-warning',
  danger: '--ds-danger',
  info: '--ds-info',
  gray: '--ds-gray-500'
};

const DS_SEMANTIC_FALLBACK = {
  primary: '#2456D6',
  success: '#12B76A',
  warning: '#F79009',
  danger: '#F04438',
  info: '#0BA5EC',
  gray: '#6B7B92'
};

const semanticCache = {};

/**
 * 取语义色（success / warning / danger / info / primary / gray）。
 * @param {string} name
 * @returns {string}
 */
export function getDsSemanticColor(name) {
  const key = DS_SEMANTIC_TOKEN[name] ? name : 'gray';
  if (semanticCache[key]) {
    return semanticCache[key];
  }
  let value = '';
  try {
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      const style = window.getComputedStyle(document.documentElement);
      value = style.getPropertyValue(DS_SEMANTIC_TOKEN[key]).trim();
    }
  } catch (e) {
    // 取不到计算样式时静默回退，不影响图表渲染
  }
  const result = value || DS_SEMANTIC_FALLBACK[key];
  semanticCache[key] = result;
  return result;
}

/**
 * 清空缓存（主要用于热更新或主题切换场景）。
 */
export function resetDsChartPaletteCache() {
  cachedPalette = null;
  Object.keys(semanticCache).forEach((key) => delete semanticCache[key]);
}

export default {
  DS_CHART_FALLBACK,
  DS_CHART_FALLBACK_PALETTE,
  getDsChartPalette,
  getDsChartColor,
  getDsSemanticColor,
  resetDsChartPaletteCache
};
